import { html, LitElement, unsafeCSS } from 'lit';
import { customElement, property, queryAsync } from 'lit/decorators.js';
import { getCookie } from '../../_ui-core_/base/package/util/cookie';
import { dispatchCustomEvent } from '../../_ui-core_/base/package/util/event';
import { updateRobotsTag } from '../../_ui-core_/base/package/util/html-helper';
import { getTipicoCustomerId } from '../../_ui-core_/base/package/util/jwt';
import { localStorage_getOrNull } from '../../_ui-core_/base/package/util/localStorage';
import App from '../../app';
import { HeaderOptionsEventName } from '../../app/package/base/router/router-types/sidebar-types';
import { LoginStatus } from '../../app/package/base/service/login/login-domain';
import { TrackingEventSource } from '../../app/package/base/service/tracking/tracking-service';
import { STORAGE_KEY_HAS_USER_LOGGED_IN_ONCE } from '../../constants';
import style from './help-center-page.css?inline';

/**
 * Example config:
 *   UUID__c: '123e4567-e89b-12d3-a456-426614174000';
 *   Provided_First_Name__c: 'John';
 *   Provided_Last_Name__c: 'Doe';
 *   SuppliedEmail: 'john.doe@example.com';
 *   Username__c: 'johndoe123';
 *   User_App__c: 'MOBILE_WEB_RETH';
 *   consent_category: "all";
 *   consent_done: "true";
 *   consent_done_date: "Sun, 22 Mar 2026 10:24:20 GMT";
 */
type HelpCenterConfig = {
  // Required by help page
  UUID__c: string;
  Provided_First_Name__c: string;
  Provided_Last_Name__c: string;
  SuppliedEmail: string;
  Username__c: string;
  User_App__c: UserAppChannel;
  // Required by Cookie Consent Manager
  _evidon_consent_cookie?: string;
  _evidon_suppress_notification_cookie?: string;
  consent_category?: string;
  consent_done?: string;
  consent_done_date?: string;
};

// Mobile web: MOBILE_WEB
// Desktop Web: DESKTOP_WEB
// Android app: ANDROID_APP
// iOS App: IOS_APP
const UserAppChannel = {
  MOBILE_WEB: 'MOBILE_WEB',
  DESKTOP_WEB: 'DESKTOP_WEB',
  ANDROID_APP: 'ANDROID_APP',
  IOS_APP: 'IOS_APP',
} as const;

type UserAppChannel = (typeof UserAppChannel)[keyof typeof UserAppChannel];

const CName = 'help-center-page';

@customElement(CName)
export class HelpCenterPage extends LitElement {
  static readonly styles = unsafeCSS(style);

  @property({ type: String }) tail = '';

  @queryAsync('iframe') private readonly _iframeRef!: Promise<HTMLIFrameElement>;
  private readonly _src = window.$app.appConfig.helpCenterIframeUrl
    ? window.$app.appConfig.helpCenterIframeUrl
    : 'src/pages/help-center/helpcenter-fallback.html';

  private _iframeElement: HTMLIFrameElement | null = null;
  private readonly _boundOnIframeLoaded = this._onIframeLoaded.bind(this);

  connectedCallback(): void {
    super.connectedCallback();
    updateRobotsTag('noindex');
    App.instance.router.scrollToTop();
    dispatchCustomEvent(this, HeaderOptionsEventName, {
      title: '',
      trackingSource: TrackingEventSource.HELP_CENTER,
    });
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this._iframeElement?.removeEventListener('load', this._boundOnIframeLoaded);
  }

  firstUpdated(): void {
    const hasUserLoggedInOnce = localStorage_getOrNull(STORAGE_KEY_HAS_USER_LOGGED_IN_ONCE);
    this.style.setProperty('--_mobile-navbar-height', hasUserLoggedInOnce ? '70px' : '205px');

    // Wait for iframe to be ready…
    window.$app.logger.log('Wait for iframe content to be ready…');
    this._iframeRef.then((iframeElement) => {
      window.$app.logger.log('Wait for iframe content to be ready… Done.');
      this._iframeElement = iframeElement;
      this._iframeElement.addEventListener('load', this._boundOnIframeLoaded);
    });
  }

  render() {
    let src = this._src;
    if (this.tail !== '') {
      src = `${src}${this.tail}`;
    }
    return html`
      <main>
        <iframe src=${src} title="Help Center"></iframe>
      </main>
    `;
  }

  private _onIframeLoaded() {
    this._createHelpCenterConfig().then((config) => {
      // Send config to iframe…
      window.$app.logger.log('Send config to iframe', config);
      try {
        this._iframeElement!.contentWindow?.postMessage(JSON.stringify(config), this._src);
      } catch (error) {
        window.$app.logger.error('Failed to send config to iframe:', error);
      }
    });
  }

  private _getUserAppChannel(): UserAppChannel {
    if (window.$app.device.isMWeb) {
      return UserAppChannel.MOBILE_WEB;
    }
    if (window.$app.device.isNativeAppIos) {
      return UserAppChannel.IOS_APP;
    }
    if (window.$app.device.isNativeAppAndroid) {
      return UserAppChannel.ANDROID_APP;
    }
    return UserAppChannel.DESKTOP_WEB;
  }

  private async _createHelpCenterConfig(): Promise<HelpCenterConfig> {
    const loggedOutConfig: HelpCenterConfig = {
      UUID__c: '',
      Provided_First_Name__c: '',
      Provided_Last_Name__c: '',
      SuppliedEmail: '',
      Username__c: '',
      User_App__c: this._getUserAppChannel(),
    };

    // Add cookie consent values to the config
    const evidonConsentCookie = getCookie('_evidon_consent_cookie');
    if (evidonConsentCookie !== null) {
      loggedOutConfig._evidon_consent_cookie = evidonConsentCookie;
    }
    const evidonSuppressNotificationCookie = getCookie('_evidon_suppress_notification_cookie');
    if (evidonSuppressNotificationCookie !== null) {
      loggedOutConfig._evidon_suppress_notification_cookie = evidonSuppressNotificationCookie;
    }
    const consentCategory = getCookie('consent_category');
    if (consentCategory !== null) {
      loggedOutConfig.consent_category = consentCategory;
    }
    const consentDone = getCookie('consent_done');
    if (consentDone !== null) {
      loggedOutConfig.consent_done = consentDone;
    }
    const consentDoneDate = getCookie('consent_done_date');
    if (consentDoneDate !== null) {
      loggedOutConfig.consent_done_date = consentDoneDate;
    }

    const loginStatus = await App.instance.login.getFinalLoginStatus();
    if (loginStatus !== LoginStatus.LOGGED_IN) {
      window.$app.logger.log('User not logged in.');
      return loggedOutConfig;
    }
    const userId = getTipicoCustomerId(window.$app.loginStore.value.jwt);

    if (userId === undefined) {
      window.$app.logger.error('Cannot get user ID from jwt.', window.$app.loginStore.value);
      return loggedOutConfig;
    }

    const firstName =
      window.$app.loginStore.value.profile?.firstName ?? window.$app.loginStore.value.profile?.screenName ?? 'noname';
    const lastName =
      window.$app.loginStore.value.profile?.lastName ?? window.$app.loginStore.value.profile?.screenName ?? 'noname';
    const username = window.$app.loginStore.value.profile?.username ?? 'noname';
    const email = window.$app.loginStore.value.profile?.email ?? 'noemail@noemail.com';
    return {
      ...loggedOutConfig,
      UUID__c: userId,
      Provided_First_Name__c: firstName,
      Provided_Last_Name__c: lastName,
      SuppliedEmail: email,
      Username__c: username,
      User_App__c: this._getUserAppChannel(),
    } as HelpCenterConfig;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    [CName]: HelpCenterPage;
  }
}
